import { useEffect, useRef } from 'react'

import { GRID_DOM_HEADER, GRID_DOM_OWNERSHIP } from './grid.const'
import { gridSelectionActions } from './grid.store'
import { GridContextMenu } from './GridContextMenu'
import { GridHotkeys } from './GridHotkeys'

export function Grid({ children }: React.PropsWithChildren) {
  // #region ============== EVENTS

  /**
   * Event handler attached to the document.
   * If anything other than a cell is clicked, cell selection is cleared.
   *
   * Uses `composedPath()`, which is fixed at dispatch: an earlier handler can
   * re-render and detach `e.target`, so `e.target.closest()` would miss the
   * cell.
   */
  const clearSelectionHandler = (e: Event) => {
    if (!(e.target instanceof HTMLElement)) return

    const targetIsNotCell = !e
      .composedPath()
      .some((node) => node instanceof Element && node.matches('[role="cell"]'))

    if (targetIsNotCell) {
      gridSelectionActions.clear()
    }
  }

  /**
   * Prevents browser defaults for drag and drop on the entire document.
   *
   * By default browsers will show the "copy" cursor on drag and open the file
   * in a new tab on drop. This hook prevents that.
   *
   * Components that need drag and drop can register local event handlers and
   * call `preventDefault()` and/or `stopPropagation()` to override behaviour as
   * needed.
   */
  const handleDragOver = (e: DragEvent) => {
    if (!(e.target instanceof Element)) return

    // Exclude other draggable features
    const isEditor = e.target.closest('.tiptap') !== null // Ignore TipTap editors (drag to upload)
    const isColumnHeaders = e.target.closest(`#${GRID_DOM_HEADER}`) !== null // Ignore column headers (drag to sort)

    if (isEditor || isColumnHeaders) return

    // Prevent drop for all document targets
    e.preventDefault()
    e.stopPropagation()

    // Prevent 'copy' cursor for all document targets
    if (!e.dataTransfer) return
    e.dataTransfer.dropEffect = 'none'
  }

  useEffect(() => {
    document.addEventListener('click', clearSelectionHandler)
    // We only need to handle `dragover` to prevent the behaviour.
    // The other drag* events are for visual changes on enter/leave/drop.
    document.addEventListener('dragover', handleDragOver)
    return () => {
      document.removeEventListener('click', clearSelectionHandler)
      document.removeEventListener('dragover', handleDragOver)
    }
  }, [])

  // #endregion

  const gridRef = useRef<HTMLDivElement | null>(null)
  const gridContextMenuRef = useRef<HTMLDivElement | null>(null)

  return (
    <div
      ref={gridRef}
      role="grid"
      aria-owns={GRID_DOM_OWNERSHIP}
      aria-label="Rundown grid"
      className="pb-6"
    >
      {children}

      <GridContextMenu ref={gridContextMenuRef} />

      <GridHotkeys
        gridRef={gridRef}
        gridContextMenuRef={gridContextMenuRef}
      />
    </div>
  )
}
